import {
  useLayoutEffect,
  useState,
  type JSX,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { Link } from 'react-router-dom';
import { Bell, ChevronDown, type LucideIcon } from 'lucide-react';
import { Images } from '@/assets/images';
import { HelpButton } from '@/components/help/HelpButton';
import { OrganizationSwitcher } from '@/components/PageHeader/OrganizationSwitcher';
import { RoleBasedRender } from '@/components/rbac/RoleBasedRender';
import { useContentScale } from '@/components/shared/layout/useContentScale';
import { Button } from '@/components/ui/button';
import { useGuardedNavigation } from '@/hooks/useGuardedNavigation';
import type { RolesConfig } from '@/hooks/useRoleCheck';
import { safeLocalStorage } from '@/lib/safeLocalStorage';
import { cn } from '@/lib/utils';

const { LogoWithText } = Images;
const COLLAPSED_SECTIONS_ITEM = 'ui.ui-v2-collapsed-sections';
/** Fixed sidebar width (Tailwind `w-60`), also what the content scale is based on. */
const SIDEBAR_WIDTH = 240;

export interface UiV2NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  active: boolean;
  roles?: RolesConfig;
  /** Runs when the item is clicked while inactive, just before navigating. */
  onNavigate?: () => void;
}

export interface UiV2NavSection {
  /** Untitled sections render without a header and can't collapse. */
  title?: string;
  items: UiV2NavItem[];
}

interface UiV2ShellProps {
  /** Sidebar sections; the last one is pinned to the bottom (Settings). */
  sections: UiV2NavSection[];
  /** Heading for routes without a sidebar entry (e.g. a project detail page). */
  title: string;
  subheader?: string;
  /** Where the logo goes (the org Dashboard when the viewer can see it). */
  homeTo: string;
  alertsTo: string;
  onSwitchToClassic: () => void;
  children: ReactNode;
}

/**
 * Opt-in UI v2 shell (kirin_ui.html): a top bar with breadcrumb, a sectioned
 * sidebar, and the mockup's warmer light palette. Tags `<html>` with `ui-v2`
 * while mounted so the token overrides in index.css also reach portaled
 * dialogs and popovers, and drop the moment the user switches back.
 */
export function UiV2Shell({
  sections,
  title,
  subheader,
  homeTo,
  alertsTo,
  onSwitchToClassic,
  children,
}: UiV2ShellProps): JSX.Element {
  const navigate = useGuardedNavigation();
  const [collapsed, setCollapsed] = useState<string[]>(() => {
    // Anything but an array (hand-edited storage) would crash every mount.
    const stored = safeLocalStorage.getItem(COLLAPSED_SECTIONS_ITEM);
    return Array.isArray(stored) ? stored : [];
  });
  const toggleSection = (title: string): void => {
    const next = collapsed.includes(title)
      ? collapsed.filter(t => t !== title)
      : [...collapsed, title];
    safeLocalStorage.setItem(COLLAPSED_SECTIONS_ITEM, next);
    setCollapsed(next);
  };

  // Layout effect: the class must be on <html> before the first paint, or a
  // reload into UI v2 flashes the classic palette, footer and page titles.
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.classList.add('ui-v2');
    return () => root.classList.remove('ui-v2');
  }, []);
  const contentScale = useContentScale(SIDEBAR_WIDTH);

  const activeSection = sections.find(s => s.items.some(i => i.active));
  const heading = activeSection?.items.find(i => i.active)?.label ?? title;

  const go =
    (to: string, onNavigate?: () => void) =>
    (e: MouseEvent<HTMLAnchorElement>): void => {
      // Modifier clicks open a new tab; leave those to the browser.
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      onNavigate?.();
      navigate(to);
    };

  // The top bar is 64px (h-16) like the Classic page header, so in-page
  // offsets written against it (`sticky top-16`, `calc(100vh - 11rem)`) hold.
  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-40 flex h-16 items-center border-b bg-card">
        <Link
          to={homeTo}
          onClick={go(homeTo)}
          aria-label="Kirin home"
          className="flex h-full w-60 shrink-0 items-center overflow-hidden border-r px-3"
        >
          <LogoWithText
            aria-hidden="true"
            className="h-20 w-auto shrink-0 fill-foreground"
          />
        </Link>
        <nav
          aria-label="Breadcrumb"
          className="hidden min-w-0 flex-1 items-center gap-1.5 px-5 text-[12.5px] text-muted-foreground md:flex"
        >
          {activeSection?.title && (
            <>
              <span className="truncate">{activeSection.title}</span>
              <span aria-hidden="true" className="text-muted-foreground/50">
                /
              </span>
            </>
          )}
          <span className="truncate font-medium text-foreground">
            {heading}
          </span>
        </nav>
        <div className="ml-auto flex shrink-0 items-center gap-2.5 pr-5">
          <Button
            variant="outline"
            size="sm"
            className="border-muted-foreground/40 flex-col gap-0 py-0 text-[11px] leading-[1.15]"
            onClick={onSwitchToClassic}
          >
            {/* The space keeps the accessible name "Go back to Classic UI";
                whitespace between flex items isn't rendered. */}
            <span>Go back to</span> <span>Classic UI</span>
          </Button>
          <Link
            to={alertsTo}
            onClick={go(alertsTo)}
            aria-label="Alerts"
            className="flex h-8 w-8 items-center justify-center rounded-md border hover:bg-muted"
          >
            <Bell className="h-4 w-4" />
          </Link>
          <HelpButton screenTitle={heading} />
          <div className="min-w-0 max-w-[280px]">
            <OrganizationSwitcher />
          </div>
        </div>
      </header>

      <aside className="fixed bottom-0 left-0 top-16 z-30 flex w-60 flex-col overflow-y-auto border-r bg-card px-2 py-1.5">
        {sections.map((section, index) => {
          const sectionTitle = section.title;
          const isOpen = !sectionTitle || !collapsed.includes(sectionTitle);
          const hasActive = section.items.some(i => i.active);
          return (
            <div
              key={sectionTitle ?? index}
              className={cn(
                'flex flex-col',
                index === sections.length - 1 && 'mt-auto'
              )}
            >
              {sectionTitle && (
                <button
                  type="button"
                  onClick={() => toggleSection(sectionTitle)}
                  aria-expanded={isOpen}
                  className={cn(
                    'flex items-center justify-between gap-1 whitespace-nowrap rounded px-2 pb-0.5 pt-2 text-left text-[10px] font-bold uppercase tracking-[.05em] hover:text-foreground',
                    // A collapsed section still shows where the current page lives.
                    !isOpen && hasActive
                      ? 'text-accent-foreground'
                      : 'text-muted-foreground/70'
                  )}
                >
                  {sectionTitle}
                  <ChevronDown
                    aria-hidden="true"
                    className={cn(
                      'h-3 w-3 shrink-0 transition-transform',
                      !isOpen && '-rotate-90'
                    )}
                  />
                </button>
              )}
              {isOpen &&
                section.items.map(item => (
                  <RoleBasedRender key={item.to} roles={item.roles}>
                    <Link
                      to={item.to}
                      onClick={go(
                        item.to,
                        item.active ? undefined : item.onNavigate
                      )}
                      aria-current={item.active ? 'page' : undefined}
                      className={cn(
                        'flex items-center gap-2.5 rounded-md px-2.5 py-1 text-[13px] transition-colors',
                        item.active
                          ? 'bg-accent font-medium text-accent-foreground'
                          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                      )}
                    >
                      <item.icon className="h-4 w-4 shrink-0" />
                      <span className="truncate">{item.label}</span>
                    </Link>
                  </RoleBasedRender>
                ))}
            </div>
          );
        })}
        <a
          href="mailto:support@knostic.ai"
          className="mt-2 px-2.5 pb-0.5 text-xs text-muted-foreground hover:text-foreground"
        >
          Contact support
        </a>
      </aside>

      <main className="ml-60 min-w-[320px]">
        <div className="px-7 pb-5 pt-7" style={contentScale}>
          <div className="mb-6">
            <h1 className="text-xl font-semibold tracking-tight">{heading}</h1>
            {subheader && (
              <p className="mt-1 text-[13px] text-muted-foreground">
                {subheader}
              </p>
            )}
          </div>
          {children}
        </div>
      </main>
    </div>
  );
}
